/**
 * 文件定位：
 * 1. 当前文件位于 Worker Blend 几何工具层；
 * 2. 负责把 Partial Edge Parameters 分割后逐段求解的 Shape 回拼成单一实体；
 * 3. 不依赖圆角或倒角具体定义，供同类局部边线特征复用。
 *
 * 文件职责：
 * 1. 封装 Puppy CAD 内核 Fuse 构造签名选择；
 * 2. 统一释放回拼过程中的临时 Shape；
 * 3. 为圆角、倒角的局部边线求解提供一致的错误上下文。
 */

type PuppyKernel = ReturnType<typeof import('../../../engine/loader').getPuppyKernel>;

type DisposableShape = {
    /** Puppy CAD 内核 临时对象释放入口。 */
    delete?: () => void;
};

/**
 * 安全释放 Puppy CAD 内核 临时对象。
 *
 * @param value 待释放对象。
 */
function safeDelete(value: unknown): void {
    const deleteMethod = (value as DisposableShape | null)?.delete;
    if (typeof deleteMethod !== 'function') {
        return;
    }
    try {
        deleteMethod.call(value);
    } catch {
        /**
         * 回拼阶段的释放失败只代表绑定对象生命周期已经结束。
         * 这里不覆盖上层真实几何错误，避免错误消息丢失关键上下文。
         */
    }
}

/**
 * 构建 Puppy CAD 内核 进度范围对象。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @returns 进度范围对象；绑定缺失时返回 undefined。
 */
function buildProgressRange(puppy: PuppyKernel): unknown {
    const ctor = (puppy as Record<string, unknown>).Message_ProgressRange_1 as
        | (new () => unknown)
        | undefined;
    return typeof ctor === 'function' ? new ctor() : undefined;
}

/**
 * 对两个 Shape 执行 Fuse 回拼。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param leftShape 左侧 Shape。
 * @param rightShape 右侧 Shape。
 * @param operationLabel 操作标签。
 * @returns 回拼后的 Shape。
 */
function buildFuseShape(
    puppy: PuppyKernel,
    leftShape: unknown,
    rightShape: unknown,
    operationLabel: string,
): unknown {
    const progress = buildProgressRange(puppy);
    const withProgressCtor = (puppy as Record<string, unknown>).BRepAlgoAPI_Fuse_3 as
        | (new (left: unknown, right: unknown, progress: unknown) => Record<string, unknown>)
        | undefined;
    const basicCtor = (puppy as Record<string, unknown>).BRepAlgoAPI_Fuse_1 as
        | (new (left: unknown, right: unknown) => Record<string, unknown>)
        | undefined;
    if (typeof withProgressCtor !== 'function' && typeof basicCtor !== 'function') {
        safeDelete(progress);
        throw new Error(`当前 Puppy CAD 内核 绑定缺少 ${operationLabel} 回拼所需的 Fuse 能力`);
    }

    const builder = typeof withProgressCtor === 'function'
        ? new withProgressCtor(leftShape, rightShape, progress)
        : new (basicCtor as new (left: unknown, right: unknown) => Record<string, unknown>)(leftShape, rightShape);

    try {
        const build = builder.Build as ((progressArg?: unknown) => void) | undefined;
        if (typeof build === 'function') {
            build.call(builder, progress);
        }
        const isDone = builder.IsDone as (() => boolean) | undefined;
        if (typeof isDone === 'function' && !isDone.call(builder)) {
            throw new Error(`${operationLabel} 回拼失败，Fuse 未完成构建`);
        }
        const shapeGetter = builder.Shape as (() => unknown) | undefined;
        const fusedShape = typeof shapeGetter === 'function' ? shapeGetter.call(builder) : null;
        if (!fusedShape) {
            throw new Error(`${operationLabel} 回拼失败，Fuse 未返回有效 Shape`);
        }
        return fusedShape;
    } finally {
        safeDelete(progress);
        safeDelete(builder);
    }
}

/**
 * 回拼 Partial Edge Parameters 求解产生的多个 Shape。
 *
 * 设计说明：
 * 1. 局部边线会先把源实体切成多个 Solid，求解器必须在各自 Solid 上注册边段；
 * 2. 每个局部求解结果和未参与求解的剩余 Solid 需要通过 Fuse 重新合并；
 * 3. 只有逐段求解的结果 Shape 是本函数需要释放的临时输入，源分割 Shape 由上游 cleanup 统一释放。
 *
 * @param input 回拼输入。
 * @returns 回拼后的 Shape。
 */
export function fusePartialBlendShapes(input: {
    /** Puppy CAD 内核 实例。 */
    puppy: PuppyKernel;
    /** 参与回拼的 Shape，包含局部求解结果和未修改的剩余实体。 */
    shapes: unknown[];
    /** 回拼结束后需要释放的临时求解结果 Shape。 */
    temporaryInputShapes: unknown[];
    /** 操作标签，用于错误消息。 */
    operationLabel: string;
}): unknown {
    if (input.shapes.length === 0) {
        throw new Error(`${input.operationLabel} 回拼缺少可融合的实体`);
    }
    if (input.shapes.length === 1) {
        return input.shapes[0]!;
    }

    const intermediateShapes: unknown[] = [];
    let currentShape = input.shapes[0]!;
    try {
        for (let index = 1; index < input.shapes.length; index += 1) {
            const nextShape = buildFuseShape(input.puppy, currentShape, input.shapes[index]!, input.operationLabel);
            if (index > 1) {
                intermediateShapes.push(currentShape);
            }
            currentShape = nextShape;
        }
        input.temporaryInputShapes.forEach((shape) => safeDelete(shape));
        intermediateShapes.forEach((shape) => safeDelete(shape));
        return currentShape;
    } catch (error) {
        input.temporaryInputShapes.forEach((shape) => safeDelete(shape));
        intermediateShapes.forEach((shape) => safeDelete(shape));
        safeDelete(currentShape);
        throw error;
    }
}
